/**
 * UnsavedChanges — Provider + hook + Dialog global pour prévenir la perte de
 * modifications non sauvegardées (Chunk 9.P.2.R2).
 *
 * Pattern réutilisable pour tous les forms de l'app (Companies Create/Edit,
 * Settings, futurs Project/Submissions). Couvre :
 *   - Navigation interne React Router (NavLink AppShell, Link Detail→Edit, etc.)
 *     via `useBlocker` (data router stable v6.7+, requires `createBrowserRouter`).
 *   - Browser back/forward arrows (popstate) — couvert auto par useBlocker.
 *   - Browser close tab / reload — `beforeunload` event listener (browser-level).
 *
 * Architecture :
 *   - <UnsavedChangesProvider> wrap les routes (rendu via RootLayout dans
 *     createBrowserRouter routes).
 *   - useUnsavedChangesGuard(dirty, message) appelé par chaque form qui veut
 *     activer la garde — registre un guard auprès du Provider.
 *   - Provider gère un seul guard actif à la fois (pattern « current form
 *     dirty »). Si plusieurs forms sont rendus simultanément (rare), le
 *     dernier monté écrase le précédent — comportement attendu pour pages
 *     mono-form.
 *   - <UnsavedChangesDialog> rendu globalement par le Provider, ouvert quand
 *     le blocker passe en state="blocked".
 *
 * a11y :
 *   - Dialog focus initial sur "Rester" (anti-clic-réflexe sur "Quitter").
 *   - role="dialog" + aria-modal + aria-labelledby + aria-describedby.
 *   - Esc → stay (parité Cancel pattern Kollectivia).
 *
 * Sécurité :
 *   - Le `message` browser beforeunload est ignoré depuis 2017 (browsers
 *     affichent leur propre dialog natif générique). Notre i18n message
 *     n'est consommé que par le custom Dialog modal interne.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useBlocker } from "react-router-dom";

import { UnsavedChangesDialog } from "./UnsavedChangesDialog";

type GuardState = {
  dirty: boolean;
  message: string;
  identityParam?: string;
};

type UnsavedChangesContextValue = {
  setGuard: (state: GuardState | null) => void;
  confirmAction: (action: () => void) => void;
  /**
   * Bypass single-shot du blocker pour la prochaine navigation. Permet à
   * un form d'autoriser la navigation post-save sans déclencher le dialog
   * pendant que `setDirty(false)` est encore queue-é par React batching.
   *
   * Contrat : à appeler **immédiatement avant** `navigate(...)` dans le
   * même tick synchrone. Le ref est consommé à la première évaluation
   * du blocker, puis reset à false.
   */
  allowNavigation: () => void;
};

const UnsavedChangesContext = createContext<UnsavedChangesContextValue | null>(null);

export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  const [guard, setGuardState] = useState<GuardState | null>(null);
  const [actionPending, setActionPending] = useState(false);
  const pendingAction = useRef<(() => void) | null>(null);
  // Ref single-shot : true = la prochaine évaluation du blocker bypasse
  // (consomme + reset). Synchrone par construction (ref, pas state) → pas
  // de race avec React batching post-`setDirty(false)`.
  const allowNextNavRef = useRef(false);

  // useBlocker : intercepte les navigations React Router internes (NavLink,
  // Link, useNavigate, browser back/forward). Bloque si guard.dirty et que
  // le path change réellement (évite blocage inutile sur même URL).
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (allowNextNavRef.current) {
      allowNextNavRef.current = false;
      return false;
    }
    if (!guard?.dirty) return false;
    return (
      currentLocation.pathname !== nextLocation.pathname ||
      (!!guard.identityParam &&
        new URLSearchParams(currentLocation.search).get(guard.identityParam) !==
          new URLSearchParams(nextLocation.search).get(guard.identityParam))
    );
  });

  // beforeunload listener — browser-level (close tab, reload, navigate hors-app).
  // Le browser ignore le returnValue custom depuis 2017 mais affiche son
  // dialog natif générique tant qu'on preventDefault + set returnValue.
  useEffect(() => {
    if (!guard?.dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [guard?.dirty]);

  const setGuard = useCallback((state: GuardState | null) => {
    setGuardState(state);
  }, []);

  const allowNavigation = useCallback(() => {
    allowNextNavRef.current = true;
  }, []);

  const confirmAction = useCallback(
    (action: () => void) => {
      if (!guard?.dirty) {
        action();
        return;
      }
      pendingAction.current = action;
      setActionPending(true);
    },
    [guard?.dirty],
  );
  const ctxValue = useMemo<UnsavedChangesContextValue>(
    () => ({ setGuard, allowNavigation, confirmAction }),
    [setGuard, allowNavigation, confirmAction],
  );

  const handleStay = useCallback(() => {
    pendingAction.current = null;
    setActionPending(false);
    if (blocker.state === "blocked") blocker.reset();
  }, [blocker]);

  const handleLeave = useCallback(() => {
    const action = pendingAction.current;
    pendingAction.current = null;
    setActionPending(false);
    if (action) {
      action();
      return;
    }
    if (blocker.state === "blocked") blocker.proceed();
  }, [blocker]);

  return (
    <UnsavedChangesContext.Provider value={ctxValue}>
      {children}
      <UnsavedChangesDialog
        open={actionPending || blocker.state === "blocked"}
        message={guard?.message?.trim() ? guard.message : null}
        onStay={handleStay}
        onLeave={handleLeave}
      />
    </UnsavedChangesContext.Provider>
  );
}

type UseUnsavedChangesGuardReturn = {
  /**
   * Autorise UN bypass single-shot du blocker pour la prochaine navigation.
   *
   * Usage post-save :
   *   ```tsx
   *   await save.mutateAsync(...);
   *   allowNavigation();          // arm le ref
   *   setDirty(false);            // queue React batching
   *   navigate(`/companies/${id}`); // blocker bypasse + reset ref
   *   ```
   *
   * Contrat strict : à appeler **immédiatement avant** `navigate(...)` dans
   * le même tick synchrone. Sans navigate dans le tick suivant, le ref reste
   * armé et la prochaine navigation dirty bypasserait par erreur.
   */
  allowNavigation: () => void;
};

/**
 * Active la garde unsaved changes pour le form courant.
 *
 * Usage :
 *   ```tsx
 *   const [dirty, setDirty] = useState(false);
 *   const { allowNavigation } = useUnsavedChangesGuard(
 *     dirty,
 *     t("companies.create.unsaved_message"),
 *   );
 *   ```
 *
 * Cleanup automatique au démontage du composant (form unmount = reset guard).
 *
 * Note testing : le hook est **permissif** — si rendered sans Provider parent
 * (typique dans les tests RTL qui utilisent `<MemoryRouter>` legacy + ne wrap
 * pas avec `<UnsavedChangesProvider>`), il devient silencieusement no-op.
 * Aucun throw. En prod le Provider est toujours rendered via App.tsx →
 * comportement nominal.
 */
export function useUnsavedChangesGuard(
  dirty: boolean,
  message: string,
  identityParam?: string,
): UseUnsavedChangesGuardReturn {
  const ctx = useContext(UnsavedChangesContext);
  const setGuard = ctx?.setGuard;
  const allowNavigation = ctx?.allowNavigation ?? noopAllowNavigation;

  useEffect(() => {
    if (!setGuard) return;
    if (dirty) {
      setGuard({ dirty, message, identityParam });
    } else {
      setGuard(null);
    }
    return () => {
      // Cleanup : démontage du composant form → reset guard pour libérer
      // le blocker (anti-blocage si user reset dirty ou navigate après save).
      setGuard(null);
    };
  }, [dirty, message, identityParam, setGuard]);

  return { allowNavigation };
}

// Stable no-op pour le cas permissif (hook hors Provider). Hors-Provider =
// tests RTL legacy `<MemoryRouter>` → useBlocker n'existe pas non plus,
// donc allowNavigation n'a aucun effet observable. Référence stable pour
// éviter de re-render les consommateurs qui useEffect sur allowNavigation.
const noopAllowNavigation = () => {};

export function useConfirmUnsavedAction() {
  const context = useContext(UnsavedChangesContext);
  if (!context) throw new Error("UnsavedChangesProvider required for cabinet switching");
  return { confirmAction: context.confirmAction, allowNavigation: context.allowNavigation };
}
